import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useAttendance } from '@/context/AttendanceContext';
import {
    fetchTaskById,
    fetchTaskTimerState,
    addComment,
    deleteComment,
    deleteAttachment,
    createTask,
    updateTask,
    updateTaskStatus,
    addSubtask,
    updateSubtask,
    deleteSubtask,
    generateShareLink,
    revokeShareLink,
    improveTaskDescription,
    uploadAttachment,
    fetchTasks,
} from '../api/projectManagementApi';
import { connectSocket, releaseSocket } from '@/services/socketClient';
import { buildEstimatedTimeString, getRunningTaskSegmentMs, hmToMs, msToHM, parseDurationStringToMs, parseEstimatedMs, parseEstimatedTimeString } from './utils/timeFormatters';
import { PRIORITY_COLORS } from './constants';
import {
    createPendingAttachment,
    cleanupPendingAttachment,
    validateAttachmentFile,
} from '../utils/attachmentUtils';

const TaskDrawerContext = createContext(null);

export function TaskDrawerProvider({ children, value }) {
    const {
        taskId,
        open,
        onClose,
        closeAll,
        currentAdminId,
        currentAdmin,
        onTaskUpdated,
        employees = [],
        projects = [],
        pushTask,
        popTask,
        popTo,
        updateStackItem,
        drawerStack,
        isTop,
        hasMask,
        mode = 'view',
        parentId = null,
        selectedProject = null,
        selectedAssignee = null,
        defaultStatus = 'In Progress',
        onSuccess,
    } = value;

    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [actualTimeMs, setActualTimeMs] = useState(0);
    const [subtasks, setSubtasks] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [reviewers, setReviewers] = useState([]);
    const [additionalProjects, setAdditionalProjects] = useState([]);
    const [milestoneId, setMilestoneId] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [commentAttachments, setCommentAttachments] = useState([]); // pending files attached to a comment
    const [commentFileReferences, setCommentFileReferences] = useState([]); // referenced StorageFiles (@File mention)
    const [pendingAttachments, setPendingAttachments] = useState([]); // pending files in the Attachments tab
    const [attachmentsDragOver, setAttachmentsDragOver] = useState(false);
    const [activeTab, setActiveTab] = useState('comments');
    const [addingSubtask, setAddingSubtask] = useState(false);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [savingSubtask, setSavingSubtask] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [sharingLoading, setSharingLoading] = useState(false);
    const [shareUrl, setShareUrl] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editPriority, setEditPriority] = useState('Medium');
    const [editEstHours, setEditEstHours] = useState(0);
    const [editEstMinutes, setEditEstMinutes] = useState(0);
    const [editEffortHours, setEditEffortHours] = useState(0);
    const [editEffortMinutes, setEditEffortMinutes] = useState(0);
    const [itemType, setItemType] = useState('task'); // 'task' | 'milestone'
    const [milestoneOwner, setMilestoneOwner] = useState(null);
    const [completionCriteria, setCompletionCriteria] = useState('');
    const [deliverables, setDeliverables] = useState([]);
    const [initialChildTasks, setInitialChildTasks] = useState([]);
    const [isMilestoneModalOpen, setIsMilestoneModalOpen] = useState(false);
    const [updatingTask, setUpdatingTask] = useState(false);
    const [improvingDescription, setImprovingDescription] = useState(false);
    const [preImproveDescription, setPreImproveDescription] = useState(null); // snapshot for undo
    const [expandedProjects, setExpandedProjects] = useState({});
    const [externalRunningTasks, setExternalRunningTasks] = useState({});
    const [projectTasks, setProjectTasks] = useState([]);
    const [liveTick, setLiveTick] = useState(Date.now());
    const commentEndRef = useRef(null);
    const { attendance, status: attendanceStatus, startTask, stopTask, refreshSession } = useAttendance() || {};

    const handleAddInitialChildTask = (title) => {
        if (!title.trim()) return;
        setInitialChildTasks((prev) => [...prev, title.trim()]);
    };

    const handleRemoveInitialChildTask = (index) => {
        setInitialChildTasks((prev) => prev.filter((_, idx) => idx !== index));
    };

    const loadTask = useCallback(async () => {
        if (!taskId && mode !== 'create') return;
        setLoading(true);
        setLoadError(false);
        try {
            if (mode === 'create') {
                const projObj = projects?.find(p => String(p._id) === String(selectedProject?._id || selectedProject));
                const defaultPm = projObj?.projectManager;
                const initialMsId = (parentId && parentId !== 'new') ? parentId : null;
                // Initialize an empty mock task for creation
                const mockTask = {
                    _id: 'new',
                    title: '',
                    description: '',
                    itemType: itemType || 'task',
                    priority: 'Medium',
                    status: defaultStatus || 'In Progress',
                    project: selectedProject ? {
                        _id: typeof selectedProject === 'object' ? selectedProject._id : selectedProject,
                        name: typeof selectedProject === 'object' ? selectedProject.name : null
                    } : null,
                    assignee: selectedAssignee ? {
                        _id: typeof selectedAssignee === 'object' ? selectedAssignee._id : selectedAssignee
                    } : null,
                    milestoneId: initialMsId,
                    collaborators: [],
                    reviewers: [],
                    subtasks: [],
                    comments: [],
                    attachments: [],
                    dependencies: [],
                };
                setTask(mockTask);
                setMilestoneId(initialMsId);
                setEditTitle('');
                setEditDescription('');
                setEditPriority('Medium');
                setEditEstHours(0);
                setEditEstMinutes(0);
                setEditEffortHours(0);
                setEditEffortMinutes(0);
                setMilestoneOwner(defaultPm || selectedAssignee || null);
                setCompletionCriteria('');
                setDeliverables([]);
                setInitialChildTasks([]);
                setActualTimeMs(0);
                setSubtasks([]);
                setCollaborators([]);
                setAdditionalProjects([]);
                setExternalRunningTasks({});
                setLoading(false);
                return;
            }

            const { data } = await fetchTaskById(taskId);
            const loadedTask = data.result;
            setTask(loadedTask);
            setItemType(loadedTask.itemType || 'task');
            if (updateStackItem && loadedTask?.title) {
                updateStackItem(taskId, loadedTask.title, loadedTask.itemType || 'task', loadedTask.parentId);
            }
            const loadedMsId = loadedTask.milestoneId?._id || loadedTask.milestoneId || null;
            setMilestoneId(loadedMsId);
            setMilestoneOwner(loadedTask.milestoneOwner || null);
            setCompletionCriteria(loadedTask.completionCriteria || '');
            setDeliverables(loadedTask.deliverables || []);
            setActualTimeMs(loadedTask.actualTimeMs || 0);

            const childList = (loadedTask.itemType === 'milestone' && Array.isArray(loadedTask.childTasks) && loadedTask.childTasks.length > 0)
                ? loadedTask.childTasks
                : (loadedTask.subtasks || []);

            setSubtasks([...childList].sort((a, b) => (a.order || 0) - (b.order || 0)));
            setCollaborators(loadedTask.collaborators || []);
            setReviewers(loadedTask.reviewers || []);
            setAdditionalProjects(loadedTask.additionalProjects || []);
            setEditTitle(loadedTask.title || '');
            setEditDescription(loadedTask.description || '');
            setEditPriority(loadedTask.priority || 'Medium');
            const est = parseEstimatedTimeString(loadedTask.estimatedTime || '');
            setEditEstHours(est.h);
            setEditEstMinutes(est.m);
            const effort = msToHM(loadedTask.actualTimeMs || 0);
            setEditEffortHours(effort.h);
            setEditEffortMinutes(effort.m);

            if (loadedTask.shareToken && loadedTask.shareTokenExpiresAt && new Date(loadedTask.shareTokenExpiresAt) > new Date()) {
                setShareUrl(`${window.location.origin}/shared/task/${loadedTask.shareToken}`);
            } else {
                setShareUrl(null);
            }

            // â”€â”€ Restore live timer state on open â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
            // If another employee was already running this task, the drawer won't
            // receive the socket event that was fired earlier. Fetch attendance state
            // directly so the play/stop button and live ticker are correct immediately.
            try {
                const { data: timerData } = await fetchTaskTimerState(taskId);
                if (timerData?.result) {
                    const { attendanceId, activeTaskEntry } = timerData.result;
                    setExternalRunningTasks({ [attendanceId]: activeTaskEntry });
                } else {
                    setExternalRunningTasks({});
                }
            } catch {
                // Non-critical â€” timer state will still update via socket
            }
        } catch {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => {
        if (open) {
            if (taskId && isTop) loadTask();
            const loadAllTasks = async () => {
                try {
                    const { data } = await fetchTasks({ limit: 'all' });
                    setProjectTasks(data?.result || []);
                } catch { /* ignore */ }
            };
            loadAllTasks();
        }
        if (!open) {
            setTask(null);
            setCommentText('');
            setLoadError(false);
            setCommentAttachments([]);
            setPendingAttachments([]);
            setAttachmentsDragOver(false);
            setActualTimeMs(0);
            setSubtasks([]);
            setCollaborators([]);
            setReviewers([]);
            setAdditionalProjects([]);
            setMilestoneId(null);
            setProjectTasks([]);
            setActiveTab('comments');
            setAddingSubtask(false);
            setNewSubtaskTitle('');
            setShareUrl(null);
            setEditTitle('');
            setEditDescription('');
            setEditPriority('Medium');
            setEditEstHours(0);
            setEditEstMinutes(0);
            setEditEffortHours(0);
            setEditEffortMinutes(0);
            setPreImproveDescription(null);
            setExternalRunningTasks({});
        }
    }, [open, taskId, isTop, loadTask]);

    useEffect(() => {
        if (task?.comments?.length) {
            commentEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [task?.comments?.length]);

    useEffect(() => {
        if (!open || !taskId || !currentAdminId) return;
        const socket = connectSocket({ userId: currentAdminId, role: currentAdmin?.role });
        if (!socket) return;
        const effortHandler = (payload) => {
            if (String(payload?.taskId) !== String(taskId)) return;
            if (typeof payload.actualTimeMs === 'number') {
                setActualTimeMs(payload.actualTimeMs);
            }
            loadTask();
        };
        const taskUpdatedHandler = (payload) => {
            if (String(payload?.taskId) === String(taskId)) {
                loadTask();
            }
        };
        const attendanceHandler = (payload) => {
            const attendanceRecord = payload?.attendance;
            const attendanceId = attendanceRecord?._id;
            if (!attendanceId) return;

            const activeTask = attendanceRecord?.dailyTask?.find((dailyTask) => {
                const linkedTaskId = dailyTask?.projectTaskRef?._id || dailyTask?.projectTaskRef;
                return String(linkedTaskId || '') === String(taskId)
                    && String(attendanceRecord?.activeTaskId || '') === String(dailyTask?._id || '');
            });

            setExternalRunningTasks((prev) => {
                const next = { ...prev };
                if (activeTask) next[attendanceId] = activeTask;
                else delete next[attendanceId];
                return next;
            });
        };
        socket.on('projectTask:effortUpdated', effortHandler);
        socket.on('projectTask:updated', taskUpdatedHandler);
        socket.on('attendance:state-updated', attendanceHandler);
        return () => {
            socket.off('projectTask:effortUpdated', effortHandler);
            socket.off('projectTask:updated', taskUpdatedHandler);
            socket.off('attendance:state-updated', attendanceHandler);
            releaseSocket();
        };
    }, [open, taskId, currentAdminId, currentAdmin?.role, loadTask]);

    useEffect(() => {
        const ownRunningTask = attendance?.dailyTask?.find((dailyTask) => {
            const linkedTaskId = dailyTask?.projectTaskRef?._id || dailyTask?.projectTaskRef;
            return String(linkedTaskId || '') === String(task?._id || taskId || '')
                && String(attendance?.activeTaskId || '') === String(dailyTask?._id || '');
        });
        const externalRunningTask = Object.values(externalRunningTasks)[0];
        if (!ownRunningTask && !externalRunningTask) return;
        const timer = setInterval(() => setLiveTick(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [attendance?.activeTaskId, attendance?.dailyTask, externalRunningTasks, task?._id, taskId]);

    // â”€â”€ Attachment upload helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    const validateAndAddFiles = (fileList, setter) => {
        const valid = Array.from(fileList).filter((file) => {
            const result = validateAttachmentFile(file);
            if (!result.valid) { message.warning(`${file.name}: ${result.reason}`, 5); return false; }
            return true;
        });
        if (valid.length === 0) return;
        setter((prev) => [...prev, ...valid.map(createPendingAttachment)]);
    };

    const updatePendingEntry = (setter, id, changes) =>
        setter((prev) => prev.map((f) => f.id === id ? { ...f, ...changes } : f));

    const uploadFileWithRetry = async (file, setter, maxAttempts = 3) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            try {
                updatePendingEntry(setter, file.id, { status: 'uploading', progress: 0, error: '' });
                const fd = new FormData();
                fd.append('file', file.file);
                const cfg = file.aiConfig || file.file?.aiConfig;
                if (cfg) {
                    if (cfg.folderCode) fd.append('folderCode', cfg.folderCode);
                    if (cfg.subfolderName) fd.append('subfolderName', cfg.subfolderName);
                    if (cfg.revisionRound) fd.append('revisionRound', cfg.revisionRound);
                    if (cfg.customFileName) fd.append('customFileName', cfg.customFileName);
                    if (cfg.classification) fd.append('classification', cfg.classification);
                    // Send tagged reviewers so backend can dispatch notifications
                    if (Array.isArray(cfg.taggedReviewers) && cfg.taggedReviewers.length > 0) {
                        fd.append('taggedReviewers', JSON.stringify(cfg.taggedReviewers));
                    }
                }
                const { data } = await uploadAttachment(taskId, fd, {
                    onUploadProgress: (event) => {
                        if (!event.total) return;
                        const pct = Math.round((event.loaded * 100) / event.total);
                        updatePendingEntry(setter, file.id, { progress: Math.min(pct, 95) });
                    },
                });
                updatePendingEntry(setter, file.id, { status: 'done', progress: 100, error: '', serverId: data?.result?._id });
                return data?.result || null;
            } catch (err) {
                if (attempt === maxAttempts) {
                    updatePendingEntry(setter, file.id, { status: 'error', error: err?.response?.data?.message || 'Upload failed.' });
                    return null;
                }
                await new Promise((r) => setTimeout(r, 600 * attempt));
            }
        }
        return null;
    };

    // â”€â”€ Attachments tab handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    const handleAttachmentTabFiles = (fileList) => validateAndAddFiles(fileList, setPendingAttachments);

    const handleAttachmentTabDrop = (e) => {
        e.preventDefault();
        setAttachmentsDragOver(false);
        handleAttachmentTabFiles(e.dataTransfer.files);
    };

    const handleUploadPendingAttachments = async () => {
        const toUpload = pendingAttachments.filter((f) => f.status === 'pending' || f.status === 'error');
        if (toUpload.length === 0) return;
        const results = await Promise.all(toUpload.map((f) => uploadFileWithRetry(f, setPendingAttachments)));
        const succeeded = results.filter(Boolean);
        if (succeeded.length > 0) {
            // Refresh task attachments list
            setTask((prev) => ({ ...prev, attachments: [...(prev?.attachments || []), ...succeeded] }));
            // Clear uploaded entries from pending list
            setPendingAttachments((prev) => prev.filter((f) => f.status !== 'done'));
            message.success(`${succeeded.length} file${succeeded.length > 1 ? 's' : ''} uploaded.`);
        }
        const failedCount = results.filter((r) => r === null).length;
        if (failedCount > 0) message.error(`${failedCount} file${failedCount > 1 ? 's' : ''} failed to upload.`);
    };

    const removePendingAttachment = (id) => {
        setPendingAttachments((prev) => {
            const removed = prev.find((f) => f.id === id);
            cleanupPendingAttachment(removed);
            return prev.filter((f) => f.id !== id);
        });
    };

    // â”€â”€ Comment attachment handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    const handleCommentFiles = (fileList) => validateAndAddFiles(fileList, setCommentAttachments);

    const removeCommentAttachment = (id) => {
        setCommentAttachments((prev) => {
            const removed = prev.find((f) => f.id === id);
            cleanupPendingAttachment(removed);
            return prev.filter((f) => f.id !== id);
        });
    };

    const handleAddComment = async () => {
        if (!commentText.trim() && commentAttachments.length === 0) return;
        setSubmittingComment(true);
        try {
            // Upload any comment attachments first, then post the comment
            let uploadedNames = [];
            if (commentAttachments.length > 0) {
                const results = await Promise.all(
                    commentAttachments.map((f) => uploadFileWithRetry(f, setCommentAttachments))
                );
                const succeeded = results.filter(Boolean);
                uploadedNames = succeeded.map((r) => r.originalName || r.fileName || '').filter(Boolean);
                if (succeeded.length > 0) {
                    setTask((prev) => ({ ...prev, attachments: [...(prev?.attachments || []), ...succeeded] }));
                }
            }

            // Build comment text â€” append attachment names if no text was typed
            const attachmentNote = uploadedNames.length > 0
                ? `\n📎 Attached: ${uploadedNames.join(', ')}`
                : '';
            const finalText = (commentText.trim() + attachmentNote).trim();

            if (finalText) {
                const fileRefIds = (commentFileReferences || []).map((f) => f._id || f.id);
                const { data } = await addComment(taskId, finalText, fileRefIds);
                if (data?.result) {
                    setTask((prev) => ({ ...prev, comments: [...(prev?.comments || []), data.result] }));
                }
            }
            setCommentText('');
            setCommentAttachments([]);
            setCommentFileReferences([]);
        } catch {
            message.error('Failed to add comment.');
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await deleteComment(taskId, commentId);
            setTask((prev) => ({ ...prev, comments: prev.comments.filter((c) => c._id !== commentId) }));
        } catch {
            message.error('Failed to delete comment.');
        }
    };

    const handleDeleteAttachment = async (attachmentId) => {
        try {
            await deleteAttachment(taskId, attachmentId);
            setTask((prev) => ({ ...prev, attachments: prev.attachments.filter((a) => a._id !== attachmentId) }));
            message.success('Attachment removed.');
        } catch {
            message.error('Failed to remove attachment.');
        }
    };

    const handleToggleSubtask = async (sub) => {
        // For real tasks, completed = status is 'Completed'
        const isNowCompleted = sub.status !== 'Completed';
        setSubtasks((prev) => prev.map((s) => s._id === sub._id ? { ...s, status: isNowCompleted ? 'Completed' : 'Backlog' } : s));
        try {
            await updateSubtask(taskId, sub._id, { completed: isNowCompleted });
        } catch {
            setSubtasks((prev) => prev.map((s) => s._id === sub._id ? { ...s, status: sub.status } : s));
            message.error('Failed to update subtask.');
        }
    };

    // Opens the subtask in the Task Drawer stack (pushes onto the navigation stack)
    const handleOpenSubtask = (sub) => {
        if (pushTask) {
            pushTask(sub._id, 'view', sub.title || 'Subtask');
        }
    };

    const handleAddSubtask = async () => {
        if (!newSubtaskTitle.trim()) return;
        setSavingSubtask(true);
        try {
            const { data } = await addSubtask(taskId, newSubtaskTitle.trim());
            if (data?.result) setSubtasks((prev) => [...prev, data.result]);
            setNewSubtaskTitle('');
            setAddingSubtask(false);
        } catch {
            message.error('Failed to add subtask.');
        } finally {
            setSavingSubtask(false);
        }
    };

    const handleDeleteSubtask = async (subId) => {
        const previous = [...subtasks];
        setSubtasks((items) => items.filter((item) => item._id !== subId));
        try {
            await deleteSubtask(taskId, subId);
        } catch {
            setSubtasks(previous);
            message.error('Failed to delete subtask.');
        }
    };

    const handleSubtaskTitleEdit = async (sub, newTitle) => {
        if (!newTitle.trim() || newTitle.trim() === sub.title) return;
        const title = newTitle.trim();
        setSubtasks((prev) => prev.map((s) => s._id === sub._id ? { ...s, title } : s));
        try {
            await updateSubtask(taskId, sub._id, { title });
        } catch {
            setSubtasks((prev) => prev.map((s) => s._id === sub._id ? { ...s, title: sub.title } : s));
            message.error('Failed to update subtask.');
        }
    };

    const handleSubtaskDragEnd = async (result) => {
        // Drag-to-reorder is a UI-only operation for now — subtasks are real tasks
        // and ordering is preserved client-side without a backend call
        if (!result.destination || result.destination.index === result.source.index) return;
        const reordered = Array.from(subtasks);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        setSubtasks(reordered);
    };


    const handlePrimaryProjectChange = async (projectId) => {
        const proj = projectId ? projects.find(p => p._id === projectId) : null;
        const optimistic = proj ? { _id: proj._id, name: proj.name, color: proj.color } : null;
        const previous = task?.project;
        const previousMsId = milestoneId;
        setTask(prev => ({ ...prev, project: optimistic, milestoneId: null }));
        setMilestoneId(null);
        // In create mode, just update local state — no backend call yet
        if (mode === 'create') return;
        // For existing tasks, persist the change immediately
        try {
            const { data } = await updateTask(taskId, { project: projectId || null, milestoneId: null });
            setTask(prev => ({ ...prev, project: data?.result?.project || optimistic, milestoneId: null }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask(prev => ({ ...prev, project: previous, milestoneId: previousMsId }));
            setMilestoneId(previousMsId);
            message.error('Failed to update project.');
        }
    };

    const handleMilestoneChange = async (newMilestoneId) => {
        const msId = newMilestoneId || null;
        const previousMsId = milestoneId;
        setMilestoneId(msId);
        setTask(prev => prev ? ({ ...prev, milestoneId: msId }) : prev);
        if (mode === 'create') return;
        try {
            const { data } = await updateTask(taskId, { milestoneId: msId });
            const savedMsId = data?.result?.milestoneId?._id || data?.result?.milestoneId || msId;
            setMilestoneId(savedMsId);
            setTask(prev => prev ? ({ ...prev, milestoneId: data?.result?.milestoneId || msId }) : prev);
            onTaskUpdated?.(data?.result);
        } catch {
            setMilestoneId(previousMsId);
            setTask(prev => prev ? ({ ...prev, milestoneId: previousMsId }) : prev);
            message.error('Failed to update milestone.');
        }
    };

    const handleCreateTask = async () => {
        const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');
        const itemLabel = isMilestone ? 'Milestone' : 'Task';

        if (!editTitle.trim()) {
            message.warning(`${itemLabel} title is required.`);
            return;
        }
        if (!task?.project?._id) {
            message.warning('Please select a project.');
            return;
        }

        setUpdatingTask(true);
        try {
            const estimatedTime = buildEstimatedTimeString(editEstHours, editEstMinutes);
            const payload = {
                title: editTitle.trim(),
                description: editDescription?.trim(),
                project: task.project._id,
                itemType: isMilestone ? 'milestone' : 'task',
                milestoneOwner: milestoneOwner?._id || milestoneOwner || undefined,
                completionCriteria: completionCriteria?.trim() || '',
                deliverables: deliverables || [],
                assignee: isMilestone ? undefined : task.assignee?._id,
                collaborators: isMilestone ? [] : collaborators.map(c => c._id || c),
                reviewers: isMilestone ? [] : reviewers.map(r => r._id || r),
                priority: editPriority,
                status: task.status,
                startDate: task.startDate,
                dueDate: task.dueDate,
                estimatedTime: isMilestone ? undefined : estimatedTime,
                parentId: (parentId && parentId !== 'new') ? parentId : undefined,
                milestoneId: milestoneId || ((parentId && parentId !== 'new') ? parentId : undefined),
            };

            const { data } = await createTask(payload);
            const createdTask = data?.result;

            // If creating a milestone and initialChildTasks were specified, create them now!
            if (isMilestone && initialChildTasks.length > 0 && createdTask?._id) {
                for (const childTitle of initialChildTasks) {
                    try {
                        await createTask({
                            title: childTitle,
                            project: createdTask.project._id || createdTask.project,
                            milestoneId: createdTask._id,
                            status: 'Backlog',
                            priority: editPriority || 'Medium',
                        });
                    } catch (e) {
                        console.error('Failed to create initial child task:', e);
                    }
                }
            }

            // Handle pending attachments
            const uploadTargets = pendingAttachments.filter((file) => file.status !== 'done');
            if (uploadTargets.length > 0) {
                for (const entry of uploadTargets) {
                    const fd = new FormData();
                    fd.append('file', entry.file);
                    try {
                        await uploadAttachment(createdTask._id, fd);
                    } catch (e) {
                        console.error('Failed to upload attachment', e);
                    }
                }
            }

            message.success(`${itemLabel} created successfully!`);
            onSuccess?.(createdTask);
            // Replace the 'new' stack item with the actual task ID
            if (popTask && pushTask) {
                popTask();
                pushTask(createdTask._id, 'view', createdTask.title);
            }
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to create task.');
        } finally {
            setUpdatingTask(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (!task || newStatus === task.status) return;
        const previousStatus = task.status;
        setTask((current) => ({ ...current, status: newStatus }));
        if (mode === 'create') return;
        setStatusUpdating(true);
        try {
            await updateTaskStatus(taskId, newStatus);
            onTaskUpdated?.({ ...task, status: newStatus });
        } catch {
            setTask((current) => ({ ...current, status: previousStatus }));
            message.error('Failed to update status.');
        } finally {
            setStatusUpdating(false);
        }
    };

    const handleCompleteTask = () => handleStatusChange('Completed');

    const handleShare = async () => {
        const idToShare = task?._id || taskId;
        if (!idToShare) return;
        setSharingLoading(true);
        try {
            const { data } = await generateShareLink(idToShare);
            if (data?.result?.token) {
                const url = `${window.location.origin}/shared/task/${data.result.token}`;
                setShareUrl(url);
                message.success('Public guest share link generated!');
                return url;
            }
        } catch (err) {
            console.error('[TaskDrawer] Share error:', err);
            message.error(err?.response?.data?.message || 'Failed to generate share link');
        } finally {
            setSharingLoading(false);
        }
    };

    const handleRevokeShare = async () => {
        const idToShare = task?._id || taskId;
        if (!idToShare) return;
        setSharingLoading(true);
        try {
            await revokeShareLink(idToShare);
            setShareUrl(null);
            message.success('Public guest link revoked.');
        } catch (err) {
            console.error('[TaskDrawer] Revoke share error:', err);
            message.error(err?.response?.data?.message || 'Failed to revoke share link');
        } finally {
            setSharingLoading(false);
        }
    };

    const copyInternalLink = () => {
        const identifier = task?.taskId || task?._id || taskId;
        if (!identifier) return;
        const internalUrl = `${window.location.origin}/project-management?task=${identifier}`;
        navigator.clipboard?.writeText?.(internalUrl);
        message.success('Internal team link copied to clipboard!');
        return internalUrl;
    };

    const getTargetUserId = () => {
        const assigneeId = task?.assignee?._id || task?.assignee;
        return assigneeId ? String(assigneeId) : String(currentAdminId);
    };

    const getProjectTaskAttendanceInfo = () => ({
        taskGid: null,
        parent: null,
        customField: null,
        customFieldTwo: null,
        estimatedField: null,
        AllotedHours: 0,
        totalEffortTime: 0,
        utilizedField: null,
        extraHourTask: false,
        sectionGid: null,
        sectionName: null,
        estimatedTime: task?.estimatedTimeMs || task?.estimatedTime || 0,
        projectTaskRef: task?._id || taskId,
        taskSource: 'pm_dashboard',
        targetUserId: getTargetUserId(),
    });

    const handleStartTimer = async () => {
        if (!task) return;

        const targetUserId = getTargetUserId();
        const isSelf = targetUserId === String(currentAdminId);

        if (isSelf) {
            if (attendanceStatus !== 'in' || !attendance) {
                message.warning('Please mark time in before starting task tracking.');
                return;
            }
            if (attendance?.isBreaking) {
                message.warning('End your break before starting task tracking.');
                return;
            }
            if (attendance?.activeTaskId) {
                message.warning('A task is already running. Stop it before starting this one.');
                return;
            }
        }

        const payload = getProjectTaskAttendanceInfo();
        const result = await startTask?.(task.title || task.description || 'Project task', payload);
        if (result?.success) {
            await refreshSession?.();
            message.success(isSelf ? 'Task timer started.' : 'Task timer started for assignee.');
        }
    };

    const handleStopTimer = async (progressUpdate = null) => {
        const targetUserId = getTargetUserId();
        const isSelf = targetUserId === String(currentAdminId);

        let activeTask = null;
        if (isSelf) {
            activeTask = attendance?.dailyTask?.find((dailyTask) => String(dailyTask?._id) === String(attendance?.activeTaskId));
        } else {
            activeTask = Object.values(externalRunningTasks)[0];
        }

        if (!activeTask) return;

        const enrichedTask = {
            ...activeTask,
            isActive: true,
            taskBreakDuration: 0,
            endTime: new Date().toISOString(),
            targetUserId: targetUserId
        };

        const result = await stopTask?.(enrichedTask, progressUpdate);
        if (result?.success) {
            await refreshSession?.();
            message.success(isSelf ? 'Task timer stopped.' : 'Task timer stopped for assignee.');
        }
    };

    const handleAddCollaborator = async (employeeId) => {
        if (!employeeId) return;
        if (collaborators.some((collaborator) => (collaborator._id || collaborator) === employeeId)) return;
        if ((task?.assignee?._id || task?.assignee) === employeeId) {
            message.warning('This person is already the assignee.');
            return;
        }
        const previous = [...collaborators];
        const employee = employees.find((item) => item._id === employeeId);
        const optimistic = employee ? { _id: employee._id, name: employee.name, surname: employee.surname, photo: employee.photo } : { _id: employeeId };
        setCollaborators((prev) => [...prev, optimistic]);
        if (mode === 'create') return;
        try {
            const newIds = [...collaborators.map((collaborator) => collaborator._id || collaborator), employeeId];
            const { data } = await updateTask(taskId, { collaborators: newIds });
            setCollaborators(data?.result?.collaborators || [...collaborators, optimistic]);
            onTaskUpdated?.(data?.result);
        } catch {
            setCollaborators(previous);
            message.error('Failed to add collaborator.');
        }
    };

    const handleRemoveCollaborator = async (collabId) => {
        const previous = [...collaborators];
        setCollaborators((prev) => prev.filter((collaborator) => (collaborator._id || collaborator) !== collabId));
        if (mode === 'create') return;
        try {
            const newIds = collaborators.filter((collaborator) => (collaborator._id || collaborator) !== collabId).map((collaborator) => collaborator._id || collaborator);
            const { data } = await updateTask(taskId, { collaborators: newIds });
            setCollaborators(data?.result?.collaborators || collaborators.filter((collaborator) => (collaborator._id || collaborator) !== collabId));
            onTaskUpdated?.(data?.result);
        } catch {
            setCollaborators(previous);
            message.error('Failed to remove collaborator.');
        }
    };


    const handleAddReviewer = async (employeeId) => {
        if (!employeeId) return;
        if (reviewers.some((reviewer) => (reviewer._id || reviewer) === employeeId)) return;
        if ((task?.assignee?._id || task?.assignee) === employeeId) {
            message.warning('This person is already the assignee.');
            return;
        }
        const previous = [...reviewers];
        const employee = employees.find((item) => item._id === employeeId);
        const optimistic = employee ? { _id: employee._id, name: employee.name, surname: employee.surname, photo: employee.photo } : { _id: employeeId };
        setReviewers((prev) => [...prev, optimistic]);
        if (mode === 'create') return;
        try {
            const newIds = [...reviewers.map((reviewer) => reviewer._id || reviewer), employeeId];
            const { data } = await updateTask(taskId, { reviewers: newIds });
            setReviewers(data?.result?.reviewers || [...reviewers, optimistic]);
            onTaskUpdated?.(data?.result);
        } catch {
            setReviewers(previous);
            message.error('Failed to add reviewer.');
        }
    };

    const handleRemoveReviewer = async (reviewerId) => {
        const previous = [...reviewers];
        setReviewers((prev) => prev.filter((reviewer) => (reviewer._id || reviewer) !== reviewerId));
        if (mode === 'create') return;
        try {
            const newIds = reviewers
                .filter((reviewer) => (reviewer._id || reviewer) !== reviewerId)
                .map((reviewer) => reviewer._id || reviewer);
            const { data } = await updateTask(taskId, { reviewers: newIds });
            setReviewers(data?.result?.reviewers || previous.filter((reviewer) => (reviewer._id || reviewer) !== reviewerId));
            onTaskUpdated?.(data?.result);
        } catch {
            setReviewers(previous);
            message.error('Failed to remove reviewer.');
        }
    };
    const handleAddProject = async (projectId) => {
        if (!projectId) return;
        if (String(projectId) === String(task?.project?._id || task?.project)) {
            message.warning('This is already the primary project.');
            return;
        }
        if (additionalProjects.some((project) => String(project._id || project) === String(projectId))) return;
        const previous = [...additionalProjects];
        const project = projects.find((item) => item._id === projectId);
        const optimistic = project ? { _id: project._id, name: project.name, color: project.color } : { _id: projectId };
        setAdditionalProjects((prev) => [...prev, optimistic]);
        if (mode === 'create') return;
        try {
            const newIds = [...additionalProjects.map((item) => item._id || item), projectId];
            const { data } = await updateTask(taskId, { additionalProjects: newIds });
            setAdditionalProjects(data?.result?.additionalProjects || [...additionalProjects, optimistic]);
            onTaskUpdated?.(data?.result);
        } catch {
            setAdditionalProjects(previous);
            message.error('Failed to add project association.');
        }
    };

    const handleRemoveAdditionalProject = async (projId) => {
        const previous = [...additionalProjects];
        setAdditionalProjects((prev) => prev.filter((project) => String(project._id || project) !== String(projId)));
        if (mode === 'create') return;
        try {
            const newIds = additionalProjects.filter((project) => String(project._id || project) !== String(projId)).map((project) => project._id || project);
            const { data } = await updateTask(taskId, { additionalProjects: newIds });
            setAdditionalProjects(data?.result?.additionalProjects || previous.filter((project) => String(project._id || project) !== String(projId)));
            onTaskUpdated?.(data?.result);
        } catch {
            setAdditionalProjects(previous);
            message.error('Failed to remove project association.');
        }
    };

    const handleImproveDescription = async () => {
        if (!editDescription.trim()) {
            message.warning('Please enter a description before using Improve with AI.');
            return;
        }
        setPreImproveDescription(editDescription); // save snapshot for undo
        setImprovingDescription(true);
        try {
            const { data } = await improveTaskDescription(taskId || 'new', editDescription);
            if (data?.result) {
                setEditDescription(data.result);
                message.success('Description improved! Review and save when ready.');
            }
        } catch {
            message.error('AI failed to improve the description. Please try again.');
        } finally {
            setImprovingDescription(false);
        }
    };

    const handleUndoImproveDescription = () => {
        if (preImproveDescription !== null) {
            setEditDescription(preImproveDescription);
            setPreImproveDescription(null);
        }
    };

    const handleUpdateTask = async () => {
        if (!taskId) return;
        setUpdatingTask(true);
        try {
            const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');
            const estimatedTime = buildEstimatedTimeString(editEstHours, editEstMinutes);
            const actualTimeMsOverride = hmToMs(editEffortHours, editEffortMinutes);
            const payload = {
                title: editTitle,
                description: editDescription,
                priority: editPriority,
                itemType: isMilestone ? 'milestone' : 'task',
                milestoneOwner: milestoneOwner?._id || milestoneOwner || null,
                completionCriteria: completionCriteria || '',
                deliverables: deliverables || [],
                estimatedTime,
                actualTimeMs: actualTimeMsOverride,
            };

            const { data } = await updateTask(taskId, payload);
            const updated = data?.result || payload;

            setTask((prev) => ({ ...prev, ...updated }));
            if (typeof updated?.actualTimeMs === 'number') setActualTimeMs(updated.actualTimeMs);

            setEditTitle(updated?.title || editTitle);
            setEditDescription(updated?.description || editDescription);
            setEditPriority(updated?.priority || editPriority || 'Medium');

            const est = parseEstimatedTimeString(updated?.estimatedTime || estimatedTime || '');
            setEditEstHours(est.h);
            setEditEstMinutes(est.m);

            const effort = msToHM(typeof updated?.actualTimeMs === 'number' ? updated.actualTimeMs : actualTimeMsOverride);
            setEditEffortHours(effort.h);
            setEditEffortMinutes(effort.m);

            onTaskUpdated?.(data?.result || { ...(task || {}), ...payload });
            message.success('Task updated.');
        } catch {
            message.error('Failed to update task.');
        } finally {
            setUpdatingTask(false);
        }
    };

    const handleAssigneeChange = async (newAssigneeId) => {
        const optimisticAssignee = newAssigneeId ? employees.find(e => String(e._id) === String(newAssigneeId)) : null;
        setTask((prev) => ({ ...prev, assignee: optimisticAssignee || (newAssigneeId ? { _id: newAssigneeId } : null) }));
        if (mode === 'create') return; // don't hit API until task is saved
        const previous = task.assignee;
        try {
            const { data } = await updateTask(taskId, { assignee: newAssigneeId || null });
            setTask((prev) => ({ ...prev, assignee: data?.result?.assignee }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, assignee: previous }));
            message.error('Failed to update assignee.');
        }
    };

    const handleDueDateChange = async (date) => {
        const newDate = date ? date.toISOString() : null;
        setTask((prev) => ({ ...prev, dueDate: newDate }));
        if (mode === 'create') return;
        const previous = task.dueDate;
        try {
            const { data } = await updateTask(taskId, { dueDate: newDate });
            setTask((prev) => ({ ...prev, dueDate: data?.result?.dueDate }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, dueDate: previous }));
            message.error('Failed to update due date.');
        }
    };

    const handleStartDateChange = async (date) => {
        const newDate = date ? date.toISOString() : null;
        setTask((prev) => ({ ...prev, startDate: newDate }));
        if (mode === 'create') return;
        const previous = task.startDate;
        try {
            const { data } = await updateTask(taskId, { startDate: newDate });
            setTask((prev) => ({ ...prev, startDate: data?.result?.startDate }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, startDate: previous }));
            message.error('Failed to update start date.');
        }
    };

    const handleAddDependency = async (blockerTaskId, depType = 'finish_to_start') => {
        if (!blockerTaskId) return;
        const current = task.dependencies || [];
        if (current.some(d => String(d.taskId?._id || d.taskId) === String(blockerTaskId))) return;
        const newDep = { taskId: blockerTaskId, type: depType };
        const optimistic = [...current, newDep];
        setTask((prev) => ({ ...prev, dependencies: optimistic }));
        if (mode === 'create') return;
        try {
            const { data } = await updateTask(taskId, {
                dependencies: optimistic.map(d => ({
                    taskId: d.taskId?._id || d.taskId,
                    type: d.type,
                })),
            });
            setTask((prev) => ({ ...prev, dependencies: data?.result?.dependencies || optimistic }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, dependencies: current }));
            message.error('Failed to add dependency.');
        }
    };

    const handleRemoveDependency = async (blockerTaskId) => {
        const current = task.dependencies || [];
        const updated = current.filter(d => String(d.taskId?._id || d.taskId) !== String(blockerTaskId));
        setTask((prev) => ({ ...prev, dependencies: updated }));
        if (mode === 'create') return;
        try {
            const { data } = await updateTask(taskId, {
                dependencies: updated.map(d => ({
                    taskId: d.taskId?._id || d.taskId,
                    type: d.type,
                })),
            });
            setTask((prev) => ({ ...prev, dependencies: data?.result?.dependencies || updated }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, dependencies: current }));
            message.error('Failed to remove dependency.');
        }
    };

    const toggleProjectExpanded = (projId) => {
        setExpandedProjects((prev) => ({ ...prev, [projId]: !prev[projId] }));
    };

    const ownActiveAttendanceTask = attendance?.dailyTask?.find((dailyTask) => {
        const linkedTaskId = dailyTask?.projectTaskRef?._id || dailyTask?.projectTaskRef;
        return String(linkedTaskId || '') === String(task?._id || taskId || '');
    });
    const externalActiveAttendanceTask = Object.values(externalRunningTasks)[0];
    const activeAttendanceTask = ownActiveAttendanceTask || externalActiveAttendanceTask;
    const isTimerRunning = Boolean(activeAttendanceTask && (
        String(attendance?.activeTaskId || '') === String(activeAttendanceTask?._id || '')
        || Boolean(externalActiveAttendanceTask)
    ));
    const activeTaskDurationMs = parseDurationStringToMs(activeAttendanceTask?.duration);
    const runningSegmentMs = isTimerRunning ? getRunningTaskSegmentMs(activeAttendanceTask, liveTick) : 0;


    const displayActualTimeMs = actualTimeMs + runningSegmentMs;
    const estimatedMs = parseEstimatedMs(task?.estimatedTime);
    const remainingTimeMs = estimatedMs > 0 ? Math.max(0, estimatedMs - displayActualTimeMs) : null;
    const isOverrun = estimatedMs > 0 && displayActualTimeMs > estimatedMs;
    const completedSubtasks = (subtasks || []).filter((sub) => sub.status === 'Completed').length;
    const priorityStyle = PRIORITY_COLORS[task?.priority] || PRIORITY_COLORS.Medium;
    const isCompleted = task?.status === 'Completed';
    const collaboratorIds = new Set((collaborators || []).map((collaborator) => String(collaborator._id || collaborator)));
    const reviewerIds = new Set((reviewers || []).map((reviewer) => String(reviewer._id || reviewer)));
    const assigneeId = String(task?.assignee?._id || task?.assignee || '');
    const availableEmployees = (employees || []).filter((employee) => !collaboratorIds.has(String(employee._id)) && String(employee._id) !== assigneeId);
    const availableReviewers = (employees || []).filter((employee) => !reviewerIds.has(String(employee._id)) && String(employee._id) !== assigneeId);
    const addedProjectIds = new Set((additionalProjects || []).map((project) => String(project._id || project)));
    const primaryProjectId = String(task?.project?._id || task?.project || '');
    const availableProjects = (projects || []).filter((project) => !addedProjectIds.has(String(project._id)) && String(project._id) !== primaryProjectId);

    // Compute available milestones for the current primary project
    const targetProjectId = primaryProjectId || (selectedProject ? String(selectedProject._id || selectedProject) : '');
    const currentProj = (projects || []).find((p) => String(p._id) === String(targetProjectId));
    const embeddedMs = (currentProj?.milestones || []).map((m) => ({
        _id: String(m._id || m.id || ''),
        title: m.title || m.name || '',
        name: m.name || m.title || '',
        status: m.status || 'Backlog',
    })).filter((m) => m._id);

    const taskMs = (projectTasks || []).filter((t) => t.itemType === 'milestone' && String(t.project?._id || t.project) === String(targetProjectId)).map((t) => ({
        _id: String(t._id),
        title: t.title || t.name || '',
        name: t.title || t.name || '',
        status: t.status || 'Backlog',
    }));

    const milestoneMap = new Map();
    embeddedMs.forEach((m) => { if (m._id) milestoneMap.set(m._id, m); });
    taskMs.forEach((m) => { if (m._id) milestoneMap.set(m._id, m); });
    const projectMilestones = Array.from(milestoneMap.values());

    // Expose safe arrays so consumers never get undefined
    const safeDependencies = task?.dependencies || [];
    const safeSubtasks = subtasks || [];
    const safeCollaborators = collaborators || [];
    const safeReviewers = reviewers || [];
    const safeAdditionalProjects = additionalProjects || [];
    const safeProjectTasks = projectTasks || [];

    const contextValue = {
        taskId, open, onClose, closeAll, currentAdminId, currentAdmin,
        drawerStack, pushTask, popTask, popTo, isTop, hasMask,
        employees: employees || [],
        projects: projects || [],
        task, loading, loadError, loadTask,
        actualTimeMs, displayActualTimeMs, remainingTimeMs,
        subtasks: safeSubtasks,
        collaborators: safeCollaborators,
        reviewers: safeReviewers,
        additionalProjects: safeAdditionalProjects,
        dependencies: safeDependencies,
        projectTasks: safeProjectTasks,
        milestoneId, setMilestoneId, projectMilestones, handleMilestoneChange,
        commentText, setCommentText, submittingComment, commentEndRef,
        commentAttachments, handleCommentFiles, removeCommentAttachment,
        commentFileReferences, setCommentFileReferences,
        pendingAttachments, attachmentsDragOver, setAttachmentsDragOver,
        handleAttachmentTabFiles, handleAttachmentTabDrop,
        handleUploadPendingAttachments, removePendingAttachment,
        activeTab, setActiveTab,
        addingSubtask, setAddingSubtask, newSubtaskTitle, setNewSubtaskTitle, savingSubtask,
        statusUpdating, sharingLoading, shareUrl, setShareUrl,
        editTitle, setEditTitle, editDescription, setEditDescription, updatingTask,
        editPriority, setEditPriority, editEstHours, setEditEstHours, editEstMinutes, setEditEstMinutes, editEffortHours, setEditEffortHours, editEffortMinutes, setEditEffortMinutes,
        itemType, setItemType, milestoneOwner, setMilestoneOwner, completionCriteria, setCompletionCriteria, deliverables, setDeliverables, isMilestoneModalOpen, setIsMilestoneModalOpen,
        initialChildTasks, setInitialChildTasks, handleAddInitialChildTask, handleRemoveInitialChildTask,
        improvingDescription, preImproveDescription,
        expandedProjects, toggleProjectExpanded,
        estimatedMs, isOverrun, completedSubtasks, priorityStyle, isCompleted,
        attendance, attendanceStatus, activeAttendanceTask, isTimerRunning,
        availableEmployees, availableReviewers, availableProjects,
        handleAddComment, handleDeleteComment, handleDeleteAttachment,
        handleToggleSubtask, handleOpenSubtask, handleAddSubtask, handleDeleteSubtask, handleSubtaskTitleEdit, handleSubtaskDragEnd,
        handleStatusChange, handleCompleteTask, handleShare, handleRevokeShare, copyInternalLink, handleStartTimer, handleStopTimer,
        handleAddCollaborator, handleRemoveCollaborator,
        handleAddReviewer, handleRemoveReviewer,
        handleAddProject, handleRemoveAdditionalProject,
        handleUpdateTask, handleAssigneeChange, handleDueDateChange, handleStartDateChange,
        handleAddDependency, handleRemoveDependency,
        handleImproveDescription, handleUndoImproveDescription,
        mode, handleCreateTask, handlePrimaryProjectChange,
    };

    return (
        <TaskDrawerContext.Provider value={contextValue}>
            {children}
        </TaskDrawerContext.Provider>
    );
}

export function useTaskDrawer() {
    const context = useContext(TaskDrawerContext);
    if (!context) {
        throw new Error('useTaskDrawer must be used within TaskDrawerProvider');
    }
    return context;
}

export default TaskDrawerContext;






















